import { isMerchantId } from "~~/shared/utils/merchant.utils";

export const useAppStateStore = defineStore("AppState", () => {
  const merchants = ref<Merchant[]>([]);
  const currentMerchant = useCookie<MerchantId>("currentMerchant", {
    default: () => "diamond-dreams",
  });
  const showDetailMode = ref<boolean>(false);
  const isAppStateLoaded = ref<boolean>(false);
  const areTidsLoaded = ref<boolean>(false);
  const isLoggingIn = ref<boolean>(true);
  const { $i18n, $serverApi } = useNuxtApp();
  const route = useRoute();

  const toPlainJson = <T>(value: T): T =>
    JSON.parse(JSON.stringify(value)) as T;
  const extractTranslationsPayload = <T>(response: GFALResponse<T> | T): T => {
    if (response && typeof response === "object" && "payload" in response) {
      return (response as GFALResponse<T>).payload;
    }
    return response as T;
  };

  const getMerchants = async () => {
    const { $serverApi } = useNuxtApp();
    try {
      const response = await $serverApi<GFALResponse<Merchant[]>>(
        "/api/merchants",
        {
          headers: constructHeaders(useUserStore().authToken),
        }
      );
      merchants.value = response.payload || [];
    } catch (error: Error | any) {
    } finally {
      isAppStateLoaded.value = true;
      return merchants.value;
    }
  };

  const isMerchantActive = (merchantId: MerchantId) => {
    const merchant = merchants.value.find((m: Merchant) => m.id === merchantId);
    if (!merchant) return false;
    return merchant.status === "ACTIVE";
  };

  const isMerchantAGame = (merchantId: MerchantId) => {
    const merchant = merchants.value.find((m: Merchant) => m.id === merchantId);
    if (!merchant) return false;
    return merchant.platforms.length > 0;
  };

  const getMerchantGamePageURI = (merchantId: MerchantId) => {
    const merchant = merchants.value.find((m: Merchant) => m.id === merchantId);
    if (!merchant) return null;
    return merchant.gamePageURI;
  };

  const getMerchantActivePages = (merchantId: MerchantId) => {
    const merchant = merchants.value.find((m: Merchant) => m.id === merchantId);
    if (!merchant || !merchant.merchantPages) return null;
    return Object.entries(merchant.merchantPages)
      .filter(([_, value]) => value === true)
      .map(([key]) => key);
  };

  const getMerchantLandingPageURI = (merchantId: MerchantId) => {
    const merchant = merchants.value.find((m: Merchant) => m.id === merchantId);
    if (!merchant) return null;
    return merchant.landingPageURI;
  };

  const getTranslationsFile = async (fileName: string) => {
    if (!isMerchantId(fileName)) {
      console.warn(
        "Skipping translations fetch for invalid merchant id",
        fileName
      );
      return {
        statusCode: 400,
        message: `Invalid merchant id: ${fileName}`,
        payload: null,
      };
    }
    try {
      const path = `/api/translations/file/${fileName}`;
      const query = {
        locale: $i18n.locale.value,
      };
      let response: GFALResponse<any> | any;
      try {
        response = await $fetch<GFALResponse<any> | any>(path, { query });
      } catch {
        response = await $serverApi<GFALResponse<any> | any>(path, { query });
      }
      const payload = extractTranslationsPayload<any>(response);
      if (payload && typeof payload === "object") {
        $i18n.mergeLocaleMessage(
          $i18n.locale.value,
          toPlainJson(payload)
        );
      }
      return {
        statusCode:
          response && typeof response === "object" && "statusCode" in response
            ? Number((response as any).statusCode)
            : 200,
        message:
          response && typeof response === "object" && "message" in response
            ? String((response as any).message)
            : "ok",
        payload,
      };
    } catch (error) {
      console.error("Error fetching translations", error);
      return {
        statusCode: 500,
        message: `Error fetching ${fileName} translations`,
        payload: null,
      };
    }
  };

  const getMaintenanceStatus = async () => {
    const { $serverApi } = useNuxtApp();
    try {
      return await $serverApi<GFALResponse<any>>(`/api/status/maintenance`);
    } catch (error) {
      console.log("getMaintenanceStatus error", error);
      return null;
    }
  };

  const checkHasMerchantTids = (merchant: MerchantId) => {
    const messages = $i18n.messages.value as Record<
      string,
      Record<string, any>
    >;
    const localeMessages = messages[$i18n.locale.value] ?? {};
    return Object.prototype.hasOwnProperty.call(localeMessages, merchant);
  };

  const checkAndGetMerchantTids = async (merchant: MerchantId) => {
    if (!isMerchantId(merchant) || checkHasMerchantTids(merchant)) {
      areTidsLoaded.value = true;
      return;
    }
    await getTranslationsFile(merchant);
    areTidsLoaded.value = true;
  };
  onNuxtReady(() => {
    isLoggingIn.value = false;
    const routeMerchant = getMerchantFromRoute();
    const cookieMerchant = isMerchantId(currentMerchant.value)
      ? currentMerchant.value
      : null;
    const initialMerchant =
      routeMerchant ??
      cookieMerchant ??
      merchants.value.find((merchant) => merchant.status === "ACTIVE")?.id ??
      "diamond-dreams";
    if (currentMerchant.value !== initialMerchant) {
      currentMerchant.value = initialMerchant;
    }
    if (initialMerchant) {
      void checkAndGetMerchantTids(initialMerchant);
    } else {
      areTidsLoaded.value = true;
    }
  });

  const getMerchantFromRoute = (): MerchantId | null => {
    if (isMerchantId(route.params.merchant as string)) {
      return route.params.merchant as MerchantId;
    }
    const pathSegments = route.path.split("/").filter(Boolean);
    const merchantRoutes = ["market", "store", "craft"];
    const routeIndex = pathSegments.findIndex((segment) =>
      merchantRoutes.includes(segment)
    );

    if (routeIndex !== -1 && pathSegments[routeIndex + 1]) {
      const merchant = pathSegments[routeIndex + 1];
      if (isMerchantId(merchant)) return merchant;
    }
    return null;
  };

  const isMerchantRoute = (): boolean => {
    return (
      route.path.includes("market") ||
      route.path.includes("store") ||
      route.path.includes("craft")
    );
  };

  watch(currentMerchant, () => checkAndGetMerchantTids(currentMerchant.value));

  watch(
    route,
    () => {
      if (isMerchantRoute()) {
        const merchant = getMerchantFromRoute();
        if (merchant) currentMerchant.value = merchant;
      }
    },
    {
      immediate: true,
    }
  );

  return {
    merchants,
    currentMerchant,
    showDetailMode,
    isAppStateLoaded,
    areTidsLoaded,
    isLoggingIn,
    isMerchantActive,
    isMerchantAGame,
    getMerchantGamePageURI,
    getMerchantActivePages,
    getMerchantLandingPageURI,
    getMerchants,
    getMaintenanceStatus,
    checkAndGetMerchantTids,
    getTranslationsFile,
    getMerchantFromRoute,
    isMerchantRoute,
  };
});
